/**
 * PostHog utility functions
 * Provides helper functions for PostHog analytics tracking
 */

/**
 * Check if PostHog is available
 */
export const isPostHogAvailable = () => {
  return typeof window !== 'undefined' && window.posthog;
};

/**
 * Capture a PostHog event
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export const captureEvent = (eventName, properties = {}) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not available, event not tracked:', eventName);
    return;
  }

  try {
    window.posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error capturing PostHog event:', error);
  }
};

/**
 * Identify a user in PostHog
 * @param {string} userId - User identifier (hashed email or ID)
 * @param {object} properties - User properties
 */
export const identifyUser = (userId, properties = {}) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not available, user not identified');
    return;
  }

  try {
    window.posthog.identify(userId, properties);
  } catch (error) {
    console.error('Error identifying user in PostHog:', error);
  }
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties = {}) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not available, properties not set');
    return;
  }

  try {
    window.posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('Error setting user properties in PostHog:', error);
  }
};

/**
 * Capture an exception/error
 * @param {Error} error - Error object
 * @param {object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not available, exception not tracked');
    return;
  }

  try {
    if (window.posthog.captureException) {
      window.posthog.captureException(error, context);
    } else {
      // Fallback to regular event capture
      captureEvent('javascript_error', {
        error_message: error.message,
        error_stack: error.stack,
        error_name: error.name,
        ...context,
      });
    }
  } catch (err) {
    console.error('Error capturing exception in PostHog:', err);
  }
};

/**
 * Hash a string (simple hash for privacy)
 * @param {string} str - String to hash
 * @returns {string} Hashed string
 */
export const hashString = (str) => {
  if (!str) return '';
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Reset PostHog session (for logout or privacy)
 */
export const resetPostHog = () => {
  if (!isPostHogAvailable()) {
    return;
  }

  try {
    window.posthog.reset();
  } catch (error) {
    console.error('Error resetting PostHog:', error);
  }
};

